import dayjs, { OpUnitType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isEmpty } from 'lodash';

// At the top of DateService.ts (or wherever you configure dayjs)
dayjs.extend(utc);
dayjs.extend(timezone);

// Inside the class

// Extend dayjs with plugins
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

export default class DateService {
  // --- Formats used throughout the application ---
  public static readonly DATE_PICKER_FORMAT = 'dd/MM/yyyy'; // small letters required by component
  public static readonly FRONTEND_DATE_FORMAT = 'DD/MM/YYYY';
  public static readonly STRICT_FRONTEND_DATE_FORMATs = [
    this.FRONTEND_DATE_FORMAT,
    'D/M/YYYY'
  ];
  public static readonly FRONTEND_DATE_TIME_FORMAT = 'DD/MM/YYYY hh:mm A';
  public static readonly BACKEND_DATE_FORMAT = 'YYYY-MM-DD';

  /**
   * Parses a date string in YYYY-MM-DD format and creates a timezone-agnostic date
   */
  private static parseBackendDate(dateStr: string): dayjs.Dayjs {
    if (!dateStr) return null;

    // Parse the date in YYYY-MM-DD format strictly
    return dayjs(dateStr, this.BACKEND_DATE_FORMAT, true);
  }
  public static formatDateToLocalDisplay(date: string | Date): string {
    if (!date) return '';

    return dayjs.utc(date).local().format('DD MMMM YYYY [at] HH:mm:ss');
  }
  /**
   * Normalizes a date by stripping time and timezone information.
   * formats ensures dates that are not in strict format can still be parsed, ex: D/M/YYYY or D/MM/YYYY
   * For date strings in YYYY-MM-DD format, uses strict parsing.
   */
  private static normalizeDate(
    date: string | Date,
    formats?: string[]
  ): dayjs.Dayjs {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return this.parseBackendDate(date);
    }

    return dayjs(date, formats).startOf('day');
  }

  private static normalizeEndDate(
    date: string | Date,
    formats?: string[]
  ): dayjs.Dayjs {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return this.parseBackendDate(date);
    }

    return dayjs(date, formats).endOf('day');
  }

  public static toDateObject(date: string): Date {
    if (!date) return null;

    return dayjs(date).toDate();
  }

  /**
   * Formats the given date (string or Date) into a front-end date string (e.g., "DD/MM/YYYY").
   *
   * @example
   * DateService.formatDateFrontend('2021-12-31') // "31/12/2021"
   *
   * @param {string | Date} date - The date to format.
   * @returns {string | null} The formatted date, or `null` if `date` is falsy.
   */
  public static formatDate(date: string | Date): string {
    if (!date) {
      return null;
    }

    return this.normalizeDate(date).format(this.FRONTEND_DATE_FORMAT);
  }

  public static formatDateWithSameDay(date: string | Date): string {
      /**
   * Normalizes a date by stripping time and timezone information.
   * formats ensures dates that are not in strict format can still be parsed, ex: D/M/YYYY or D/MM/YYYY
   * For date strings in YYYY-MM-DD format, uses strict parsing.
   */

    return (dayjs(date).utc().startOf('day')).format(this.FRONTEND_DATE_FORMAT);
  }
  /**
   * Formats the given date (string or Date) into a front-end date/time string
   * (e.g., "DD/MM/YYYY hh:mm A").
   *
   * @example
   * DateService.formatDateTime('2025-01-16T11:13:16.000Z'); // "16/01/2025 11:13 AM"
   *
   * @param {string | Date} date - The date to format.
   * @returns {string | null} The formatted date/time, or `null` if `date` is falsy.
   */
  public static formatDateTime(date: string | Date): string {
    if (!date) {
      return null;
    }

    return dayjs(date).format(this.FRONTEND_DATE_TIME_FORMAT);
  }

  /**
   * Formats the given date (string or Date) into a back-end date string (e.g., "YYYY-MM-DD").
   *
   * @example
   * DateService.formatDateBackend('16-01-2025') // '2025-01-16'
   *
   * @param {string | Date} date - The date to format.
   * @returns {string | null} The formatted date, or `null` if `date` is falsy.
   */
  public static formatDateBackend(date: string | Date): string {
    if (!date) {
      return null;
    }
    if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
      return this.normalizeDate(date, this.STRICT_FRONTEND_DATE_FORMATs).format(
        this.BACKEND_DATE_FORMAT
      );
    }

    return this.normalizeDate(date).format(this.BACKEND_DATE_FORMAT);
  }

  /**
   * Returns inclusive calendar days between two dates (both start and end day count).
   * Example: 02/07/2026 to 16/07/2026 => 15 days
   */
  public static inclusiveDaysBetween(
    startDate: string | Date,
    endDate: string | Date
  ): number | null {
    const start = this.formatDateBackend(startDate);
    const end = this.formatDateBackend(endDate);

    if (!start || !end) return null;

    const days =
      this.parseBackendDate(end).diff(this.parseBackendDate(start), 'day') + 1;

    return days > 0 ? days : null;
  }

  /**
   * Formats the given date range (string or Date) into a back-end date string array with adding a day to the end date (e.g., "YYYY-MM-DD").
   *
   * @example
   * DateService.formatDateBackend(['16-01-2025', '17-01-2025']) // ['2025-01-16', '2025-01-18']
   *
   * @param {string[] | Date[]} dates - The dates to format.
   * @returns {string | null} The formatted date, or `null` if `date` is falsy.
   */
  public static formatDateRangeBackend(dates: string[] | Date[]): string[] {
    if (isEmpty(dates) || !dates?.[0] || !dates?.[1]) {
      return null;
    }

    return [
      this.normalizeDate(dates[0]).format(this.BACKEND_DATE_FORMAT),
      this.normalizeDate(dates[1])
        .add(1, 'day')
        .format(this.BACKEND_DATE_FORMAT)
    ];
  }

  /**
   * Formats the given date range (string or Date) into ISO date-time strings
   * with the end date bumped by one day.
   *
   * @param {string[] | Date[]} dates - [start, end]
   * @returns {string[] | null} [ISO start, ISO end +1 day], or null if invalid.
   */
  public static formatDateTimeRangeBackend(dates: string[] | Date[]): string[] {
    if (isEmpty(dates) || !dates?.[0] || !dates?.[1]) {
      return null;
    }

    return [
      this.normalizeDate(dates[0]).startOf('day').toISOString(),
      this.normalizeEndDate(dates[1]).endOf('day').toISOString()
    ];
  }

  /**
   * Formats a single date (string or Date) into an ISO date-time string
   * with the date bumped by one day.
   *
   * @param {string | Date} date - The date to format.
   * @returns {string | null} ISO date +1 day, or null if invalid.
   */

  public static formatDateTimeBackend(date: string | Date): string | null {
    if (!date) {
      return null;
    }

    return this.normalizeDate(date).format('YYYY-MM-DDT00:00:00.000[Z]');
  }

  public static formatDateTimeBackendSameDay(
    date: string | Date
  ): string | null {
    if (!date) {
      return null;
    }

    return this.normalizeDate(date).toDate().toISOString();
  }

  /**
   * Used mainly with Excel date columns
   * Formats the given date (string or Date) into a back-end date string (e.g., "YYYY-MM-DD") using a specific format, ensuring date is formatted exactly per the format and not the current locale.
   *
   * @example
   * DateService.formatDateBackend('16-01-2025') // '2025-01-16'
   *
   * @param {string | Date} date - The date to format.
   * @returns {string | null} The formatted date, or `null` if `date` is falsy.
   */
  public static formatDateBackendWithFormat(date: string | Date): string {
    if (!date) {
      return null;
    }

    return this.normalizeDate(date, this.STRICT_FRONTEND_DATE_FORMATs).format(
      this.BACKEND_DATE_FORMAT
    );
  }

  /**
   * Returns the end of the month if the current month is December (month === 11).
   * Otherwise, returns the date one month from now.
   *
   * @returns {Date} The computed date based on the current month.
   */
  public static getDateBasedOnMonth(): Date {
    const now = this.normalizeDate(new Date());

    return now.month() === 11 // 11 is December
      ? now.endOf('month').toDate() // Return the end of December
      : now.add(1, 'month').toDate(); // Return the date one month from now
  }

  /**
   * Calculates the age (in whole years) for a given date of birth,
   * taking into account whether the birthday has occurred yet this year.
   *
   * @param {string | Date} dob - The person's date of birth.
   * @returns {number} The calculated age in whole years.
   */
  public static getAge(dob: string | Date): number {
    const birthDate = this.normalizeDate(dob);
    const today = this.normalizeDate(new Date());

    let age = today.year() - birthDate.year();

    // If today's month/day is still before the birth month/day, subtract 1
    if (
      today.month() < birthDate.month() ||
      (today.month() === birthDate.month() && today.date() < birthDate.date())
    ) {
      age--;
    }

    return age;
  }

  /**
   * Adds the specified number of days to the given date, treating the date as UTC.
   *
   * @param {string | Date} date - The date to which to add days.
   * @param {number} days - The number of days to add.
   * @returns {Date | null} The resulting date after adding the specified days in UTC, or `null` if `date` is falsy.
   */
  public static addDays(date: string | Date, days: number): Date {
    if (!date) {
      return null;
    }

    return this.normalizeDate(date).add(days, 'days').toDate();
  }

  /**
   * Adds the specified number of months to the given date.
   *
   * @param {string | Date} date - The date to which to add months.
   * @param {number} months - The number of months to add.
   * @returns {Date} The resulting date after adding the specified months.
   */
  public static addMonths(date: string | Date, months: number): Date {
    return this.normalizeDate(date).add(months, 'months').toDate();
  }

  /**
   * Subtracts the specified number of months from the given date.
   *
   * @param {string | Date} date - The date from which to subtract months.
   * @param {number} months - The number of months to subtract.
   * @returns {Date} The resulting date after subtracting the specified months.
   */
  public static subtractMonths(date: string | Date, months: number): Date {
    return this.normalizeDate(date).subtract(months, 'months').toDate();
  }

  /**
   * Adds the specified number of years to the given date.
   *
   * @param {string | Date} date - The date to which to add years.
   * @param {number} years - The number of years to add.
   * @returns {Date} The resulting date after adding the specified years.
   */
  public static addYears(date: string | Date, years: number): Date {
    return this.normalizeDate(date).add(years, 'years').toDate();
  }

  /**
   * Checks if one date is strictly before another date.
   *
   * @param {string | Date} date - The date to compare.
   * @param {string | Date} dateToCompare - The date against which `date` is compared.
   * @returns {boolean} `true` if `date` is strictly before `dateToCompare`; otherwise, `false`.
   */
  public static isDateBefore(
    date: string | Date,
    dateToCompare: string | Date
  ): boolean {
    return this.normalizeDate(date).isBefore(this.normalizeDate(dateToCompare));
  }

  /**
   * Checks if the provided date is strictly between the given start and end dates.
   *
   * @param {string | Date} date - The date to check.
   * @param {string | Date} startDate - The start of the range.
   * @param {string | Date} endDate - The end of the range.
   * @returns {boolean} `true` if `date` is between `startDate` and `endDate`; otherwise, `false`.
   */
  public static isDateBetween(
    date: string | Date,
    startDate: string | Date,
    endDate: string | Date
  ): boolean {
    return this.normalizeDate(date).isBetween(
      this.normalizeDate(startDate),
      this.normalizeDate(endDate)
    );
  }

  /**
   * Checks whether two dates are the same based on the specified unit of time.
   *
   * If no unit is provided, the dates are compared by their exact date/time value.
   *
   * @param {string | Date} date - The date to compare.
   * @param {string | Date} compareDate - The date against which to compare.
   * @param {OpUnitType} [unit] - The unit of time for comparison (e.g. 'day', 'month', 'year').
   * @returns {boolean} `true` if both dates are the same based on the unit; otherwise, `false`.
   */
  public static isSameDate(
    date: string | Date,
    compareDate: string | Date,
    unit?: OpUnitType
  ): boolean {
    return dayjs(date).isSame(dayjs(compareDate), unit);
  }

  /**
   * Returns the difference between two dates in milliseconds.
   *
   * A positive value means `date1` is after `date2`;
   * zero means they are the same moment in time;
   * a negative value means `date1` is before `date2`.
   *
   * @param {string | Date} date1 - The first date to compare.
   * @param {string | Date} date2 - The second date to compare.
   * @returns {number} The difference in milliseconds.
   */
  public static diff(
    date1: string | Date,
    date2: string | Date,
    unit: dayjs.QUnitType | OpUnitType = 'millisecond'
  ): number {
    return dayjs(date1).diff(dayjs(date2), unit);
  }

  /**
   * Determines whether the provided value represents a valid date.
   *
   * @param {string | Date} date - The date value (string or `Date` object) to validate.
   * @returns {boolean} `true` if `date` is valid according to dayjs; otherwise, `false`.
   */
  public static isValidDate(date: string | Date): boolean {
    if (!date) {
      return false;
    }
    if (date instanceof Date) {
      return !isNaN(date.valueOf());
    }

    return dayjs(date).isValid();
  }

  /**
   * Used mainly with Excel date columns
   * Determines whether the provided value represents a valid date format as per the format provided exactly.
   *
   * @param {string | Date} date - The date value (string or `Date` object) to validate.
   * @param {string[]} format - The formats to check.
   * @returns {boolean} `true` if `date` is valid according to dayjs; otherwise, `false`.
   */
  public static isValidDateWithFormat(
    date: string | Date,
    format = this.STRICT_FRONTEND_DATE_FORMATs
  ): boolean {
    if (!date) {
      return false;
    }
    if (date instanceof Date) {
      return !isNaN(date.valueOf());
    }

    return dayjs(date, format, true).isValid();
  }

  /**
   * Checks if a given date string is valid according to the specified format.
   *
   * @param {string} date - The date string to validate.
   * @param {string} [format=this.FRONTEND_DATE_FORMAT] - The format to check against (defaults to FRONTEND_DATE_FORMAT).
   * @returns {boolean} `true` if the string is valid for the given format; otherwise, `false`.
   */
  public static isValidFormat(
    date: string,
    format = this.FRONTEND_DATE_FORMAT
  ): boolean {
    return dayjs(date, format).isValid();
  }
}
